/**
 * Site/session packet completion dashboard (admin/editor).
 * Usage: /admin/packet-dashboard?session=<id>
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Checkbox,
  Link,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import api, { getCurrentSiteId } from '../services/api';
import { useClearOnSiteChange, useCurrentSiteId } from '../hooks/useCurrentSiteId';
import { useAuth } from '../contexts/AuthContext';

function fmtIso(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function unsignedFormsSummary(row) {
  const m = row.forms_signed_map;
  if (!m || typeof m !== 'object') return '';
  const ids = Object.entries(m)
    .filter(([, v]) => !v)
    .map(([k]) => k);
  if (!ids.length) return '';
  const shown = ids.slice(0, 5).join(', ');
  return ids.length > 5 ? `${shown}…` : shown;
}

function unsignedFormsCount(row) {
  const m = row.forms_signed_map;
  if (m && typeof m === 'object') {
    return Object.values(m).filter((v) => !v).length;
  }
  const total = row.forms_total ?? 0;
  const signed = row.forms_signed ?? 0;
  return Math.max(total - signed, 0);
}

/** 'sent in last N days' select — same relative-range pattern as the medical-due filter. */
function passesSentFilter(row, filterValue) {
  if (!filterValue) return true;
  if (filterValue === 'never') return !row.sent_at;
  if (!row.sent_at) return false;
  const sent = new Date(row.sent_at);
  if (Number.isNaN(sent.getTime())) return false;
  const days = { last7: 7, last14: 14, last30: 30 }[filterValue];
  if (!days) return true;
  const cutoff = new Date();
  cutoff.setHours(0, 0, 0, 0);
  cutoff.setDate(cutoff.getDate() - days);
  return sent >= cutoff;
}

function isPacketSendEligible(row) {
  if (typeof row.packet_send_eligible === 'boolean') return row.packet_send_eligible;
  return (row.service_type || '') === 'center_based';
}

function serviceTypeLabel(serviceType) {
  const t = (serviceType || 'unknown').replace(/_/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/** Must match PacketBulkActionView.MAX_BULK_BATCH — keep in sync with backend. */
const MAX_BULK_BATCH = 25;

function chunkIds(ids, size = MAX_BULK_BATCH) {
  const chunks = [];
  for (let i = 0; i < ids.length; i += size) {
    chunks.push(ids.slice(i, i + size));
  }
  return chunks;
}

function mapBulkFailures(failed, nameById) {
  return failed.map((r) => ({
    name: nameById[r.student_id] || `Student ${r.student_id}`,
    error: r.error === 'guard'
      ? 'session calendar not configured (School Session Setup)'
      : r.error === 'not_center_based'
        ? 'not center-based (related service / SEIT excluded from packet send)'
        : r.error === 'no_packet'
          ? 'no packet sent yet — use Bulk send first'
          : (r.error || 'failed'),
  }));
}

export default function PacketDashboardPage() {
  const { user } = useAuth();
  const siteId = useCurrentSiteId();
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionParam = searchParams.get('session') || '';
  const siteParam = searchParams.get('site') || '';
  const [sessionId, setSessionId] = useState(sessionParam);
  const [siteFilter, setSiteFilter] = useState(() => {
    if (siteParam) return siteParam;
    const chromeSiteId = getCurrentSiteId();
    return chromeSiteId ? String(chromeSiteId) : '';
  });
  const [statusFilter, setStatusFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [sentFilter, setSentFilter] = useState('');
  const [unsignedFilter, setUnsignedFilter] = useState('');
  const [archivedFilter, setArchivedFilter] = useState('');
  const [reviewedFilter, setReviewedFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [dashMeta, setDashMeta] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [selected, setSelected] = useState({});
  const [confirmSend, setConfirmSend] = useState(false);
  const [bulkSummary, setBulkSummary] = useState(null);
  const [batchProgress, setBatchProgress] = useState('');
  const canBulkReset = user?.role === 'admin' || user?.role === 'superadmin';

  const load = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    setRows([]);
    setError('');
    try {
      const dashParams = { session: sessionId, detail: '1' };
      if (siteFilter.trim()) dashParams.site = siteFilter.trim();
      const [dash, ana] = await Promise.all([
        api.get(`/sessions/packet-dashboard/`, { params: dashParams }),
        api.get(`/sessions/packet-analytics/`, { params: { session: sessionId, ...(siteFilter.trim() ? { site: siteFilter.trim() } : {}) } }),
      ]);
      setRows(dash.data.results || []);
      setDashMeta({ site_id: dash.data.site_id, site_slug: dash.data.site_slug });
      setAnalytics(ana.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to load');
      setRows([]);
      setDashMeta(null);
    } finally {
      setLoading(false);
    }
    // siteId is not read inside the request. It is a dependency so a header
    // site change refetches when the session id in the URL stays the same.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, siteFilter, siteId]); // eslint-disable-line react-hooks/exhaustive-deps

  useClearOnSiteChange(siteId, () => {
    setRows([]);
    setAnalytics(null);
    setDashMeta(null);
    setSelected({});
    setLoading(true);
    setError('');
    if (!siteParam) setSiteFilter(siteId ? String(siteId) : '');
    if (!sessionParam) setSessionId('');
  });

  useEffect(() => {
    if (sessionParam) {
      setSessionId(sessionParam);
    }
    if (siteParam) {
      setSiteFilter(siteParam);
    } else {
      const chromeSiteId = getCurrentSiteId();
      if (chromeSiteId) setSiteFilter(String(chromeSiteId));
    }
  }, [sessionParam, siteParam, siteId]);

  useEffect(() => {
    if (sessionId) load();
  }, [sessionId, load]);

  // No session in the URL: default to the current (active) session for this site.
  useEffect(() => {
    if (sessionParam || sessionId) return;
    let cancelled = false;
    api.get('/sessions/current-session/')
      .then((res) => {
        if (!cancelled && res.data?.id) setSessionId(String(res.data.id));
        else if (!cancelled) setLoading(false);
      })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sessionParam, sessionId, siteId]);

  const toggle = (id) => {
    setSelected((s) => ({ ...s, [id]: !s[id] }));
  };

  const selectedIds = Object.entries(selected)
    .filter(([, v]) => v)
    .map(([k]) => Number(k));

  // All column filters are client-side on the loaded rows and combine with AND
  // (row counts are ~130-200 per site). Same approach as the original Status filter.
  const nameQuery = nameFilter.trim().toLowerCase();
  const visibleRows = rows.filter((r) => {
    if (statusFilter && r.overall !== statusFilter) return false;
    if (nameQuery && !`${r.last_name || ''}, ${r.first_name || ''}`.toLowerCase().includes(nameQuery)) return false;
    if (!passesSentFilter(r, sentFilter)) return false;
    const unsigned = unsignedFormsCount(r);
    if (unsignedFilter === 'has' && unsigned === 0) return false;
    if (unsignedFilter === 'none' && unsigned > 0) return false;
    const archived = !!r.archived_pdf_url;
    if (archivedFilter === 'yes' && !archived) return false;
    if (archivedFilter === 'no' && archived) return false;
    const isReviewed = !!(r.reviewed || r.reviewed_at);
    if (reviewedFilter === 'yes' && !isReviewed) return false;
    if (reviewedFilter === 'no' && isReviewed) return false;
    return true;
  });
  const hasColumnFilters = !!(statusFilter || nameQuery || sentFilter || unsignedFilter || archivedFilter || reviewedFilter);
  const clearColumnFilters = () => {
    setStatusFilter('');
    setNameFilter('');
    setSentFilter('');
    setUnsignedFilter('');
    setArchivedFilter('');
    setReviewedFilter('');
  };

  const sendEligibleRows = visibleRows.filter(isPacketSendEligible);
  const selectedSendEligibleIds = selectedIds.filter((id) => {
    const row = rows.find((r) => r.student_id === id);
    return row && isPacketSendEligible(row);
  });
  const selectedIneligible = rows.filter((r) => selected[r.student_id] && !isPacketSendEligible(r));

  const allEligibleSelected =
    sendEligibleRows.length > 0 && sendEligibleRows.every((r) => selected[r.student_id]);
  const someSelected = selectedIds.length > 0 && !allEligibleSelected;

  const toggleAll = () => {
    if (allEligibleSelected) {
      setSelected({});
    } else {
      const next = {};
      sendEligibleRows.forEach((r) => { next[r.student_id] = true; });
      setSelected(next);
    }
  };

  const nameById = {};
  rows.forEach((r) => { nameById[r.student_id] = `${r.last_name}, ${r.first_name}`; });
  const selectedNoEmail = rows.filter((r) => selected[r.student_id] && r.has_parent_email === false);

  const runBulkInChunks = async (action, ids) => {
    const chunks = chunkIds(ids);
    let okCount = 0;
    const failedAcc = [];
    setLoading(true);
    setError('');
    setBulkSummary(null);
    setBatchProgress('');
    try {
      for (let i = 0; i < chunks.length; i += 1) {
        setBatchProgress(`Processing batch ${i + 1} of ${chunks.length}…`);
        const res = await api.post('/sessions/packet-bulk/', {
          action,
          student_ids: chunks[i],
        });
        const results = res.data?.results || [];
        okCount += results.filter((r) => r.ok).length;
        failedAcc.push(...mapBulkFailures(results.filter((r) => !r.ok), nameById));
        setBulkSummary({ action, ok: okCount, failed: [...failedAcc] });
      }
      setSelected({});
      await load();
    } catch (e) {
      if (okCount > 0 || failedAcc.length > 0) {
        setBulkSummary({ action, ok: okCount, failed: [...failedAcc] });
      }
      setError(e.response?.data?.detail || e.message || 'Bulk action failed');
    } finally {
      setBatchProgress('');
      setLoading(false);
    }
  };

  const bulk = async (action) => {
    if (!selectedIds.length) return;
    if (action === 'reset' && !canBulkReset) return;
    await runBulkInChunks(action, selectedIds);
  };

  return (
    <Box sx={{ p: 2, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Parent packet dashboard
      </Typography>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            label="Session ID"
            value={sessionId}
            onChange={(e) => setSessionId(e.target.value)}
            sx={{ width: 200 }}
          />
          <TextField
            size="small"
            label="Site (id or slug, optional)"
            value={siteFilter}
            onChange={(e) => setSiteFilter(e.target.value)}
            placeholder="e.g. 14th-ave"
            sx={{ width: 220 }}
          />
          <Button
            variant="contained"
            onClick={() => {
              const p = { session: sessionId };
              if (siteFilter.trim()) p.site = siteFilter.trim();
              setSearchParams(p);
              load();
            }}
          >
            Load
          </Button>
          <Button
            variant="contained"
            color="success"
            disabled={!selectedSendEligibleIds.length || loading}
            onClick={() => setConfirmSend(true)}
          >
            Send packets ({selectedSendEligibleIds.length})
          </Button>
          <Button variant="outlined" disabled={!selectedIds.length || loading} onClick={() => bulk('remind')}>
            Bulk remind
          </Button>
          {canBulkReset && (
            <Button
              variant="outlined"
              color="error"
              disabled={!selectedIds.length || loading}
              onClick={() => {
                if (window.confirm('Reset parent packets for selected students? This cannot be undone.')) bulk('reset');
              }}
            >
              Bulk reset (admin)
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mt: 2 }}>
          <TextField
            size="small"
            label="Student"
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            placeholder="Name contains…"
            sx={{ width: 190 }}
          />
          <TextField
            size="small"
            select
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ width: 150 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="complete">Complete</MenuItem>
            <MenuItem value="in_progress">In progress</MenuItem>
            <MenuItem value="not_sent">Not sent</MenuItem>
          </TextField>
          <TextField
            size="small"
            select
            label="Sent"
            value={sentFilter}
            onChange={(e) => setSentFilter(e.target.value)}
            sx={{ width: 170 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="last7">Last 7 days</MenuItem>
            <MenuItem value="last14">Last 14 days</MenuItem>
            <MenuItem value="last30">Last 30 days</MenuItem>
            <MenuItem value="never">Not sent yet</MenuItem>
          </TextField>
          <TextField
            size="small"
            select
            label="Unsigned forms"
            value={unsignedFilter}
            onChange={(e) => setUnsignedFilter(e.target.value)}
            sx={{ width: 180 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="has">Has unsigned forms</MenuItem>
            <MenuItem value="none">All forms signed</MenuItem>
          </TextField>
          <TextField
            size="small"
            select
            label="Archived"
            value={archivedFilter}
            onChange={(e) => setArchivedFilter(e.target.value)}
            sx={{ width: 130 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="yes">Yes</MenuItem>
            <MenuItem value="no">No</MenuItem>
          </TextField>
          <TextField
            size="small"
            select
            label="Reviewed"
            value={reviewedFilter}
            onChange={(e) => setReviewedFilter(e.target.value)}
            sx={{ width: 150 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="yes">Reviewed</MenuItem>
            <MenuItem value="no">Not reviewed</MenuItem>
          </TextField>
          {hasColumnFilters && (
            <Button size="small" onClick={clearColumnFilters}>
              Clear filters
            </Button>
          )}
          {rows.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              Showing {visibleRows.length} of {rows.length}
            </Typography>
          )}
        </Box>
      </Paper>
      {dashMeta && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Session site: {dashMeta.site_slug || dashMeta.site_id || '—'}
          {' · '}
          Packet send targets <strong>center-based</strong> students only
          ({sendEligibleRows.length} of {rows.length} listed). Related service / SEIT students are excluded from this dashboard.
        </Typography>
      )}
      {analytics?.by_event_type?.length > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Events:{' '}
          {analytics.by_event_type.map((x) => `${x.event_type}: ${x.n}`).join(' · ')}
        </Typography>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {bulkSummary && (
        <Alert
          severity={bulkSummary.failed.length ? 'warning' : 'success'}
          sx={{ mb: 2 }}
          onClose={() => setBulkSummary(null)}
        >
          {bulkSummary.action === 'send' ? 'Sent' : bulkSummary.action === 'remind' ? 'Reminded' : 'Reset'}{' '}
          {bulkSummary.ok} student{bulkSummary.ok === 1 ? '' : 's'}.
          {bulkSummary.failed.length > 0 && (
            <>
              {' '}{bulkSummary.failed.length} failed:
              {bulkSummary.failed.slice(0, 20).map((f, i) => (
                <Typography key={i} variant="body2">• {f.name} — {f.error}</Typography>
              ))}
              {bulkSummary.failed.length > 20 && (
                <Typography variant="body2">…and {bulkSummary.failed.length - 20} more</Typography>
              )}
            </>
          )}
        </Alert>
      )}
      <Dialog open={confirmSend} onClose={() => !loading && setConfirmSend(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send parent packets?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            This will email {selectedSendEligibleIds.length} center-based famil
            {selectedSendEligibleIds.length === 1 ? 'y' : 'ies'} their individual packet link.
            Related service / SEIT students are excluded from this dashboard.
          </Typography>
          {selectedIneligible.length > 0 && (
            <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
              {selectedIneligible.length} selected non–center-based student
              {selectedIneligible.length === 1 ? '' : 's'} will be skipped on send
              (remind/reset still available for them).
            </Alert>
          )}
          {selectedNoEmail.length > 0 && (
            <Alert severity="warning" sx={{ mt: 1, maxHeight: 180, overflow: 'auto' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {selectedNoEmail.length} selected student{selectedNoEmail.length === 1 ? ' has' : 's have'} no parent
                email on file — a link will be created but no email will be delivered:
              </Typography>
              {selectedNoEmail.slice(0, 20).map((r) => (
                <Typography key={r.student_id} variant="body2">• {r.last_name}, {r.first_name}</Typography>
              ))}
              {selectedNoEmail.length > 20 && (
                <Typography variant="body2">…and {selectedNoEmail.length - 20} more</Typography>
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !loading && setConfirmSend(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            disabled={loading}
            onClick={async () => {
              setConfirmSend(false);
              // Persist only eligible ids for the send action.
              const next = {};
              selectedSendEligibleIds.forEach((id) => { next[id] = true; });
              setSelected(next);
              // selectedIds from state would be stale — pass eligible ids directly.
              if (!selectedSendEligibleIds.length) return;
              await runBulkInChunks('send', selectedSendEligibleIds);
            }}
          >
            Send {selectedSendEligibleIds.length} packet{selectedSendEligibleIds.length === 1 ? '' : 's'}
          </Button>
        </DialogActions>
      </Dialog>
      {loading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 1 }}>
          <CircularProgress />
          {batchProgress && (
            <Typography variant="body2" color="text.secondary">
              {batchProgress}
            </Typography>
          )}
        </Box>
      )}
      {!loading && sessionId && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allEligibleSelected}
                  indeterminate={someSelected}
                  onChange={toggleAll}
                  inputProps={{ 'aria-label': 'Select all center-based students' }}
                />
              </TableCell>
              <TableCell>Student</TableCell>
              <TableCell>Program</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Site</TableCell>
              <TableCell>New</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Source</TableCell>
              <TableCell align="right">Signed</TableCell>
              <TableCell>Sent</TableCell>
              <TableCell align="right">Days</TableCell>
              <TableCell>Last remind</TableCell>
                <TableCell>Unsigned forms</TableCell>
                <TableCell>Archived</TableCell>
                <TableCell>Completed</TableCell>
                <TableCell>Reviewed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((r) => (
              <TableRow key={r.student_id} hover>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={!!selected[r.student_id]}
                    onChange={() => toggle(r.student_id)}
                  />
                </TableCell>
                <TableCell>
                  <Link component={RouterLink} to={r.student_url_path || `/students/${r.student_id}`} underline="hover">
                    {r.last_name}, {r.first_name}
                  </Link>
                </TableCell>
                <TableCell>
                  <Chip
                    label={serviceTypeLabel(r.service_type)}
                    size="small"
                    color={isPacketSendEligible(r) ? 'default' : 'warning'}
                    variant="outlined"
                    title={isPacketSendEligible(r) ? 'Eligible for packet send' : 'Excluded from packet send (center-based only)'}
                  />
                </TableCell>
                <TableCell>
                  {r.has_parent_email === false
                    ? <Chip label="No email" size="small" color="warning" variant="outlined" />
                    : ''}
                </TableCell>
                <TableCell>{r.site_slug || '—'}</TableCell>
                <TableCell>{r.is_new_student ? 'Y' : ''}</TableCell>
                <TableCell>{r.overall}</TableCell>
                <TableCell>
                  {r.paper_completed ? (
                    <Chip label="Paper" size="small" color="secondary" variant="outlined" title="Completed via paper submission — no e-signature" />
                  ) : ''}
                </TableCell>
                <TableCell align="right">
                  {r.forms_signed}/{r.forms_total}
                </TableCell>
                <TableCell>{fmtIso(r.sent_at)}</TableCell>
                <TableCell align="right">{r.days_since_sent != null ? r.days_since_sent : '—'}</TableCell>
                <TableCell>{fmtIso(r.last_reminder_at)}</TableCell>
                <TableCell sx={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={unsignedFormsSummary(r)}>
                  {unsignedFormsSummary(r) || '—'}
                </TableCell>
                <TableCell>{r.archived_pdf_url ? 'Yes' : ''}</TableCell>
                <TableCell>{fmtIso(r.archived_at)}</TableCell>
                <TableCell
                  title={
                    r.reviewed || r.reviewed_at
                      ? `Reviewed by ${r.reviewed_by_name || 'staff'}${r.reviewed_at ? ` on ${fmtIso(r.reviewed_at)}` : ''}`
                      : ''
                  }
                >
                  {r.reviewed || r.reviewed_at ? fmtIso(r.reviewed_at) : ''}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
